// English texts. `ru.ts` must have exactly the same keys (a test checks it). Texts are plain
// sentences: say what happened and what to do next, the way you would to a person across a desk.
export const en = {
  app: { name: 'Form Platform' },
  common: {
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    loading: 'Loading…',
    add: 'Add',
    remove: 'Remove',
    back: 'Back',
    yes: 'Yes',
    no: 'No',
    none: 'None',
    name: 'Name',
    actions: 'Actions',
    saved: 'Saved.',
    retry: 'Try again',
    optional: 'optional',
    never: 'never',
    all: 'All',
    more: 'Load more',
    confirmDelete: 'Delete',
    loadFailed: 'This could not be loaded.',
  },
  locale: { label: 'Language', en: 'English', ru: 'Русский' },
  theme: { label: 'Theme', light: 'Light', dark: 'Dark' },
  errors: {
    generic: 'Something went wrong. Try again.',
    forbidden: 'Your role does not allow this.',
    notFound: 'This does not exist, or you do not have access to it.',
    network: 'The server could not be reached. Check your connection and try again.',
  },
  auth: {
    signIn: 'Sign in',
    signUp: 'Create account',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    newPassword: 'New password',
    yourName: 'Your name',
    passwordHint: 'At least 10 characters.',
    signInTitle: 'Sign in to your account',
    signUpTitle: 'Create your account',
    noAccount: 'No account yet?',
    haveAccount: 'Already have an account?',
    forgot: 'Forgot your password?',
    forgotTitle: 'Reset your password',
    forgotIntro: 'Enter the address you signed up with. We will send a link to choose a new password.',
    forgotSend: 'Send the link',
    forgotSent: 'If an account exists for that address, a link is on its way. It works for one hour.',
    resetTitle: 'Choose a new password',
    resetDone: 'Your password is changed. Sign in with the new one.',
    resetBadLink: 'This link is not valid any more. Ask for a new one.',
    signInFailed: 'The email or the password is not right.',
    signUpFailed: 'The account could not be created.',
    verifiedTitle: 'Your email address is confirmed',
    verifiedIntro: 'You can create forms now.',
    continue: 'Continue',
    verifyBanner: 'Confirm your email address to create forms. We sent a link to {email}.',
    verifyResend: 'Send it again',
    verifyResent: 'A new link is on its way.',
    inviteTitle: 'You are invited',
    inviteIntro: '{inviter} invited you to join {organization} as {role}.',
    inviteAccept: 'Accept and open',
    inviteSignInFirst: 'Sign in or create an account with {email} to accept the invitation.',
    inviteInvalid: 'This invitation is not valid. It may have expired, been withdrawn, or be meant for another address.',
    inviteNeedsVerified: 'Confirm your email address first, then open the invitation link again.',
    inviteSignInOnly: 'Sign in with the address the invitation was sent to.',
    inviteCreateIntro: 'Create your account to accept the invitation. The address it was sent to is yours to sign in with.',
    inviteCreate: 'Create account',
    inviteAccountExists: 'This address has an account already. Sign in to accept the invitation.',
    closedHint: 'Accounts are created by the platform team.',
    welcomeTitle: 'Choose a password',
    welcomeIntro: 'An account was created for you. Choose a password to finish setting it up.',
    welcomeDone: 'Your password is set. Sign in with it.',
  },
  welcome: {
    title: 'Create your workspace',
    intro: 'A workspace holds your sites, their forms and the people who work on them. Agencies usually create one and add a project per client site.',
    workspaceName: 'Workspace name',
    address: 'Address',
    addressHint: 'Lowercase letters, digits and dashes. It appears in links to the dashboard.',
    timezone: 'Timezone',
    create: 'Create workspace',
    failed: 'The workspace could not be created.',
    another: 'New workspace',
    closedTitle: 'No workspace yet',
    closedIntro: 'Your account is not part of any workspace yet. Workspaces are created by the platform team — ask them to add you to one, or ask a workspace admin to invite you.',
  },
  nav: {
    overview: 'Overview',
    submissions: 'Submissions',
    projects: 'Projects',
    channels: 'Channels',
    members: 'Members',
    settings: 'Settings',
    workspace: 'Workspace',
    limited: 'Your access is limited to some projects.',
    suspended: 'This workspace is suspended. Its forms do not accept submissions.',
  },
  roles: { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' },
  overview: {
    title: 'Overview',
    last24h: 'Submissions, last 24 hours',
    last7d: 'Submissions, last 7 days',
    spam7d: 'Spam caught, last 7 days',
    failed: 'Notifications that failed',
    waiting: 'Notifications waiting',
    failedHint: 'Open a submission to see why and send it again.',
    startTitle: 'Set up your first form',
    start1: 'Create a project for your site and list the addresses it is served from.',
    start2: 'Add a channel — the inbox that should get the notifications — and bind it to the project.',
    start3: 'Create a form, copy its snippet into your page, and send a test.',
    recent: 'Latest submissions',
    seeAll: 'See all',
    counts: 'Projects: {projects} · forms: {forms} · channels: {channels}',
  },
  submissions: {
    title: 'Submissions',
    search: 'Search in any field',
    project: 'Project',
    form: 'Form',
    allProjects: 'All projects',
    allForms: 'All forms',
    received: 'Received',
    spam: 'Spam',
    when: 'Received',
    from: 'From',
    summary: 'Summary',
    notifications: 'Notifications',
    empty: 'No submissions yet',
    emptyHint: 'When someone sends one of your forms, it shows up here.',
    emptyFiltered: 'Nothing matches these filters.',
    detailTitle: 'Submission',
    fields: 'What was sent',
    extra: 'Other fields the page sent',
    context: 'Where it came from',
    page: 'Page',
    ip: 'IP address',
    browser: 'Browser',
    captcha: 'CAPTCHA',
    captchaVerified: 'passed',
    captchaUnverified: 'could not be checked (provider unavailable)',
    spamReason: 'Marked as spam: {reason}',
    noDeliveries: 'No channel is bound to this form, so nobody was notified.',
    resend: 'Send again',
    channelRetired: 'Channel removed',
    channelRetiredHint: 'This channel stayed in the workspace the project came from. The history is kept; nothing more is sent through it.',
    resent: 'Queued. It will be sent in a moment.',
    attempts: 'Attempts: {count}',
    nextAttempt: 'next try {when}',
    sentAt: 'sent {when}',
    delete: 'Delete submission',
    deleteConfirm: 'Delete this submission for good? This cannot be undone.',
    deleted: 'Deleted.',
  },
  delivery: { pending: 'Waiting', sending: 'Sending', sent: 'Sent', dead: 'Failed', suppressed: 'Suppressed' },
  projects: {
    title: 'Projects',
    intro: 'One project per website. Its settings apply to all of its forms.',
    create: 'New project',
    createTitle: 'New project',
    name: 'Project name',
    nameHint: 'Usually the site\'s domain, e.g. example.com.',
    origins: 'Addresses the site is served from',
    originsHint: 'One per line, e.g. https://example.com or https://*.example.com. Only pages on these addresses may send the forms.',
    forms: 'Forms: {count}',
    empty: 'No projects yet',
    emptyHint: 'Create a project for the first site whose forms you want to handle.',
    created: 'Project created.',
    delete: 'Delete project',
    deleteConfirm: 'Delete the project "{name}" with all its forms and submissions? This cannot be undone.',
    deleted: 'Project deleted.',
    tabForms: 'Forms',
    tabSettings: 'Settings',
    tabChannels: 'Channels',
    channelsIntro: 'Every form in this project notifies the channels switched on here. A form can switch one off or add its own.',
    noOrigins: 'No address is allowed yet, so no page can send this project\'s forms. Add the site\'s address in Settings.',
  },
  forms: {
    create: 'New form',
    createTitle: 'New form',
    name: 'Form name',
    template: 'Start from',
    templates: { contact: 'Contact form — name, email, message', callback: 'Callback request — name, phone', newsletter: 'Newsletter — email only' },
    created: 'Form created.',
    empty: 'No forms in this project yet',
    emptyHint: 'Create a form, then paste its snippet into your page.',
    status: { active: 'Active', paused: 'Paused' },
    pause: 'Pause',
    resume: 'Resume',
    pausedNote: 'Paused: the form answers "not accepting submissions".',
    recent: 'Last 30 days: {count}',
    last: 'last {when}',
    key: 'Form key',
    endpoint: 'Endpoint',
    tabInstall: 'Install',
    tabFields: 'Fields',
    tabSettings: 'Settings',
    tabChannels: 'Channels',
    installIntro: 'Paste one of these into your page. The first does the most for you.',
    snippetClient: 'Form with our script (recommended)',
    snippetClientHint: 'The script keeps the anti-spam token fresh however long the page stays open, shows the CAPTCHA, retries through a short outage and fills in the messages. Keep your own markup and styles — only the attributes matter.',
    snippetHtml: 'Plain HTML form',
    snippetFetch: 'JavaScript (fetch)',
    submissionsLink: 'See its submissions',
    rename: 'Rename form',
    renamed: 'Form renamed.',
    delete: 'Delete form',
    deleteConfirm: 'Delete the form "{name}" and its submissions? Pages that still post to it will get "not found". This cannot be undone.',
    deleted: 'Form deleted.',
    needVerified: 'Confirm your email address before creating a form.',
  },
  fields: {
    intro: 'What the form accepts. Saving a change creates a new version; submissions already received keep the version they were checked against.',
    version: 'Version {version}',
    key: 'Key',
    keyHint: 'The input\'s name attribute.',
    label: 'Label',
    type: 'Type',
    required: 'Required',
    role: 'Used as',
    roleNone: '—',
    roles: { name: 'Sender\'s name', email: 'Sender\'s email (Reply-To)', phone: 'Sender\'s phone', message: 'Message', consent: 'Consent' },
    types: {
      text: 'Text',
      textarea: 'Long text',
      email: 'Email',
      phone: 'Phone',
      number: 'Number',
      select: 'Choice',
      multiselect: 'Multiple choice',
      checkbox: 'Checkbox',
      consent: 'Consent',
      date: 'Date',
      url: 'Link',
      hidden: 'Hidden',
    },
    options: 'Options',
    optionsHint: 'One per line.',
    maxLength: 'Max length',
    phoneRegions: 'Countries',
    phoneRegionsHint: 'two-letter codes, in the order to try',
    phoneRegionsPlaceholder: 'US, CA',
    phoneRegionsTried: 'A number without a country code is read as a number of {names} — the first where it is valid.',
    phoneRegionsNone: 'No countries: only numbers that start with + and a country code are accepted.',
    phoneRegionsUnknown: 'Not a country code: {codes}. The United Kingdom is GB.',
    phoneRegionsTooMany: 'At most {max} countries.',
    add: 'Add field',
    moveUp: 'Move up',
    moveDown: 'Move down',
    saved: 'Fields saved as version {version}.',
    unchanged: 'Nothing changed.',
  },
  settings: {
    inherited: 'Inherited',
    overridden: 'Overridden',
    fromProject: 'from the project',
    fromDefault: 'platform default',
    revert: 'Use the project\'s value',
    formIntro: 'A form uses its project\'s settings unless you override one here.',
    allowedOrigins: 'Allowed addresses',
    allowedOriginsHint: 'One per line. Pages on these addresses may send the form.',
    allowLocalhost: 'Allow localhost',
    allowLocalhostHint: 'For development. Switch it off for a live site.',
    locale: 'Language of messages shown to visitors',
    honeypot: 'Honeypot field',
    honeypotHint: 'A hidden input only bots fill in.',
    timingToken: 'Timing token',
    timingTokenHint: 'The page fetches a token before sending. Switch it off only for forms that must work without JavaScript.',
    minFillSeconds: 'Fastest a person can fill it in, seconds',
    unknownFieldPolicy: 'Fields the form does not define',
    unknownPolicies: { store_as_extra: 'Keep them', ignore: 'Drop them', reject: 'Refuse the submission' },
    successUrl: 'Page to show after sending (without JavaScript)',
    errorUrl: 'Page to show after a failure (without JavaScript)',
    urlHint: 'Must be on an allowed address. Leave empty to show a plain message.',
    retentionDays: 'Keep submissions for, days',
    retentionHint: 'Older submissions are deleted, within a day at most; spam after 14 days, or this period if shorter. Shortening it applies to submissions already stored; lengthening it, only to new ones.',
    retentionShortenTitle: 'Shorten the retention period?',
    retentionShortenProject: 'Submissions older than {days} days, in every form that uses the project\'s period, will be deleted — usually within the hour. They cannot be recovered.',
    retentionShortenForm: 'This form\'s submissions older than {days} days will be deleted — usually within the hour. They cannot be recovered.',
    retentionShortenApply: 'Shorten and delete',
    storeIp: 'Store the visitor\'s IP address',
    groupAccess: 'Who may send the form',
    groupSpam: 'Spam protection',
    groupBehaviour: 'Behaviour',
    groupPrivacy: 'Privacy',
  },
  captcha: {
    title: 'CAPTCHA',
    intro: 'A check that the visitor is a person, made before the form is accepted. The keys come from the provider.',
    inheritedIntro: 'This form uses its project\'s CAPTCHA. Save a choice here to give the form its own.',
    useProject: 'Use the project\'s',
    provider: 'Provider',
    providers: {
      none: 'Off',
      turnstile: 'Cloudflare Turnstile',
      recaptcha_v2: 'Google reCAPTCHA v2 (checkbox)',
      recaptcha_v3: 'Google reCAPTCHA v3 (score, nothing shown)',
    },
    where: {
      turnstile: 'Create a widget in the Cloudflare dashboard under Turnstile, and list the site\'s address there.',
      recaptcha_v2: 'Register the site in the Google reCAPTCHA admin console with the type "v2 — I\'m not a robot". Keys of another type will not work.',
      recaptcha_v3: 'Register the site in the Google reCAPTCHA admin console with the type "v3 — score based". Keys of another type will not work.',
    },
    siteKey: 'Site key',
    siteKeyHint: 'Public: it is part of the page. Copy it as the provider shows it.',
    secretKey: 'Secret key',
    secretNew: 'Stored encrypted. It cannot be shown again, only replaced. A new site key needs its own secret.',
    secretSaved: 'A secret key is saved. Leave this empty to keep it, or enter a new one to replace it.',
    minScore: 'Lowest score to accept',
    minScoreHint: 'From 0 (surely a bot) to 1 (surely a person). Google suggests 0.5.',
    saved: 'CAPTCHA saved. Forms pick it up within half a minute.',
  },
  channels: {
    title: 'Channels',
    intro: 'Where notifications go. Create a channel, then switch it on for a project or a form.',
    create: 'New channel',
    createTitle: 'New email channel',
    createWebhookTitle: 'New webhook',
    editTitle: 'Edit channel',
    type: 'Type',
    types: { email: 'Email', webhook: 'Webhook', bitrix24: 'Bitrix24' },
    noTypes: 'No channel type is available in this workspace yet. Ask support to turn one on.',
    webhookNameHint: 'For you, e.g. "CRM".',
    createBitrix24Title: 'New Bitrix24 channel',
    bitrix24NameHint: 'For you, e.g. "Leads to Bitrix24".',
    bitrix24Entity: 'Creates',
    bitrix24Entities: { lead: 'Leads', deal: 'Deals, each with a contact' },
    bitrix24EntityHint: {
      lead: 'For a CRM in the classic mode, with leads.',
      deal: 'For a CRM without leads (the simple mode), or to skip them: the name, email and phone go to a new contact, linked to the deal.',
    },
    bitrix24EntityFixed: 'Chosen when the channel was created. For the other, create another channel.',
    bitrix24HowTitle: {
      lead: 'Every submission becomes a lead in your Bitrix24. To connect it:',
      deal: 'Every submission becomes a deal in your Bitrix24, with a contact for who sent it. To connect it:',
    },
    bitrix24Step1: 'In Bitrix24, open Developer resources → Other → Inbound webhook.',
    bitrix24Step2: 'Tick the CRM permission (crm) and save.',
    bitrix24Step3: 'Copy the URL under "Webhook to call REST API" and paste it below.',
    bitrix24Url: 'Inbound webhook URL',
    bitrix24UrlHint: 'It works like a password to your CRM: it is tried now, stored encrypted and never shown again.',
    bitrix24UrlSaved: 'Saved. Paste a new URL only to replace it — it is tried before it is saved.',
    bitrix24Assigned: 'Responsible user ID',
    bitrix24AssignedHint: 'The Bitrix24 user new leads and deals are assigned to — the number in the address of their profile. Empty: Bitrix24 decides.',
    bitrix24Source: 'Source',
    bitrix24SourceHint: 'A source code from your CRM settings. WEB is Bitrix24\'s "Website".',
    bitrix24Creates: { lead: 'Creates leads', deal: 'Creates deals' },
    bitrix24CreatesAssigned: { lead: 'Creates leads · responsible user {user}', deal: 'Creates deals · responsible user {user}' },
    bitrix24Mapped: 'Fields mapped: {count}',
    mapping: {
      open: { lead: 'Field mapping', deal: 'Pipeline and fields' },
      title: { lead: 'Field mapping — {name}', deal: 'Pipeline and fields — {name}' },
      intro: {
        lead: 'Which lead field each form field goes to. Every field also stays in the lead\'s comments.',
        deal: 'Which pipeline the deals go to, and which deal field each form field goes to. Every field also stays in the deal\'s comments.',
      },
      pipeline: 'Pipeline',
      pipelineDefault: 'The default pipeline',
      pipelineDefaultNamed: 'The default pipeline ({name})',
      pipelineHint: 'New deals start at the first stage of the pipeline.',
      pipelineGone: 'Pipeline {id}',
      pipelineMissing: 'The portal no longer has this pipeline, or the webhook\'s user cannot see it. Choose another: until then Bitrix24 may refuse the deals.',
      fetched: 'Fields as read from {portal} {when}.',
      reading: 'Reading the portal\'s fields…',
      stalled: 'The portal\'s fields have not arrived. Refresh to try again.',
      never: 'The portal\'s fields have not been read yet.',
      failed: 'The fields could not be read: {message}',
      refresh: 'Refresh fields',
      fieldFor: { lead: 'Lead field for {field}', deal: 'Deal field for {field}' },
      commentsOnly: '— Only in the comments',
      standard: 'Bitrix24 fields',
      custom: 'Your fields',
      inForms: 'In: {forms}',
      noForm: 'No form has this field now; its mapping is kept.',
      missingTarget: 'The portal no longer has this field. It is dropped when you save.',
      automatic: {
        lead: { name: 'Goes to the lead\'s name by itself.', email: 'Goes to the lead\'s email by itself.', phone: 'Goes to the lead\'s phone by itself.' },
        deal: { name: 'Goes to the contact\'s name by itself.', email: 'Goes to the contact\'s email by itself.', phone: 'Goes to the contact\'s phone by itself.' },
      },
      types: { string: 'text', integer: 'whole number', double: 'number', boolean: 'yes / no', date: 'date', datetime: 'date and time', enumeration: 'list', url: 'link' },
      multiple: 'several values',
      values: 'Which list item each answer stands for:',
      valuesFree: 'What a visitor types for each list item:',
      itemFor: 'List item for {value}',
      answerFor: 'Answer that means {item}',
      skip: '— Skip',
      noForms: 'There are no forms in this workspace yet.',
      unmappable: {
        lead: 'Other lead fields — {count} of them: money, files, users, links — cannot take a form value.',
        deal: 'Other deal fields — {count} of them: money, files, users, links, the stage — cannot take a form value.',
      },
      alwaysComments: {
        lead: 'Name, email, phone, title, comments, source and the responsible user are filled by the platform.',
        deal: 'Title, comments, source, the responsible user and the contact are filled by the platform; name, email and phone go to the contact.',
      },
      saved: { lead: 'Mapping saved.', deal: 'Pipeline and fields saved.' },
    },
    url: 'Address',
    urlHint: 'Every submission is sent here as JSON by POST. It must be https:// and reachable from the internet.',
    headers: 'Headers',
    headersHint: 'Sent with every call, e.g. an API key your server expects. Values are stored encrypted and never shown again.',
    headersLine: 'Headers: {names}',
    headerName: 'Name',
    headerValue: 'Value',
    headerValueSaved: 'Saved — leave empty to keep',
    addHeader: 'Add header',
    removeHeader: 'Remove header',
    signatureNote: 'Every call is signed: the X-Formplat-Signature header lets your server check that it came from us and was not changed.',
    signingTitle: 'Signing secret',
    signingIntro: 'Copy the signing secret of "{name}" now. It is not shown again; if it is lost, make a new one.',
    signingHow: 'Your server checks each call with it: an HMAC-SHA256 of the timestamp and the body, compared with the X-Formplat-Signature header. The developer documentation has code to copy.',
    signingDone: 'I have copied it',
    rotate: 'New signing secret',
    rotateConfirm: 'Make a new signing secret for "{name}"? The current one keeps working for 24 hours, so that your server can switch. A secret replaced earlier than that stops working now.',
    previousUntil: 'The previous signing secret works until {when}.',
    name: 'Channel name',
    nameHint: 'For you, e.g. "Sales inbox".',
    recipients: 'Recipients',
    recipientsHint: 'One email address per line, up to 20.',
    subject: 'Subject',
    subjectHint: 'Placeholders like {placeholder} are replaced with the submitted value. Leave empty for the default.',
    fromName: 'Sender name',
    fromNameHint: 'Shown in the inbox. The address itself is the platform\'s.',
    empty: 'No channels yet',
    emptyHint: 'Add the inbox that should receive your form submissions.',
    created: 'Channel created.',
    deleted: 'Channel deleted.',
    deleteConfirm: 'Delete the channel "{name}"? Forms bound to it will stop notifying it.',
    health: { ok: 'Working', unverified: 'Not used yet', failing: 'Failing' },
    lastOk: 'last delivered {when}',
    failures: 'Failures in a row: {count}',
    bind: 'Notify this channel',
    bound: 'On',
    off: 'Off',
    inheritedOn: 'On (from the project)',
    disabledHere: 'Switched off for this form',
    formOnly: 'On for this form only',
    formIntro: 'Channels switched on for the project apply here too. You can switch one off for this form or add another.',
    none: 'Create a channel first, under Channels.',
    switchOffHere: 'Switch off for this form',
    switchOnHere: 'Switch on for this form',
    inherit: 'Back to the project\'s setting',
  },
  members: {
    title: 'Members',
    intro: 'People who can open this workspace.',
    invite: 'Invite',
    inviteTitle: 'Invite a member',
    role: 'Role',
    roleHints: {
      owner: 'Everything, including removing the workspace.',
      admin: 'Everything except transferring or removing the workspace.',
      member: 'Manages projects, forms and channels; reads submissions.',
      viewer: 'Reads submissions and their delivery status.',
    },
    invited: 'Invitation sent to {email}.',
    pending: 'Invitations waiting for an answer',
    expires: 'expires {when}',
    cancelInvite: 'Withdraw',
    remove: 'Remove from workspace',
    removeConfirm: 'Remove {name} from this workspace?',
    removed: 'Removed.',
    joined: 'joined {when}',
    you: 'you',
    access: 'Access',
    allProjects: 'All projects',
    someProjects: 'Projects: {count}',
    limitTitle: 'Limit access to projects',
    limitIntro: 'Choose the projects {name} may see. With none chosen, they see the whole workspace.',
    inviteLimitHint: 'Choose projects to limit what they can see. With none chosen, they see the whole workspace.',
    limitSaved: 'Access updated.',
    roleChanged: 'Role changed.',
  },
  workspace: {
    title: 'Workspace settings',
    profile: 'Profile',
    name: 'Name',
    locale: 'Language of notifications',
    timezone: 'Timezone',
    timezoneHint: 'Dates in the dashboard are shown in it, e.g. Europe/Berlin.',
    region: 'Region',
    regionHint: 'Where the data is stored. It cannot be changed.',
    account: 'Your account',
    sessions: 'Signed-in devices',
    signOutOthers: 'Sign out everywhere else',
    signedOutOthers: 'Every other device is signed out.',
    thisDevice: 'this device',
  },
};

export type Messages = typeof en;
